/**
 * Field-id aliases for grid columns that were renamed.
 *
 * Users persist grid field ids in several places that have no migration of
 * their own: URL state (filters, sort, grouping, hidden/shown columns,
 * advanced filter JSON), localStorage (default filter/sort, edit-form
 * visibility) and server-side column sets. A renamed id would fail silently
 * there (MUI drops filters on unknown fields, column sets hide unknown ids),
 * so every place that loads persisted ids passes them through these helpers.
 *
 * Release 2.1.2 (listings data model): user-maintained, platform-neutral
 * columns moved from `amazon_listings` to the `listings` supertype, and
 * Amazon's own listing id was renamed to `amazon_listing_id`. This map must
 * stay identical to the server-side column_sets remap
 * (pt-postman-codepipeline `miscellaneous_scripts/migrateColumnSetsListings`).
 *
 * The map is not table-specific: the inventory grid's nested listing columns
 * use the same ids.
 */

/** Columns lifted from `amazon_listings` to `listings` (same column name). */
export const LIFTED_LISTING_COLUMNS = [
  "listing_title",
  "html",
  "sort",
  "card_number",
  "wn",
  "wwo",
  "listory",
  "pick_status",
  "an",
  "shelf",
  "prod_list_type",
  "alpha1",
  "alpha2",
  "priority",
  "num1",
  "num2",
  "num3",
  "bcp",
  "orders",
  "threading_notes",
  "curation_notes",
  "inventory_notes",
  "pricing_notes",
  "is_competitor_listing",
  "created_at",
] as const;

function buildFieldAliases(): Readonly<Record<string, string>> {
  const aliases: Record<string, string> = {};
  LIFTED_LISTING_COLUMNS.forEach((column) => {
    aliases[`amazon_listings.${column}`] = `listings.${column}`;
  });
  aliases["amazon_listings.listing_id"] = "amazon_listings.amazon_listing_id";
  return Object.freeze(aliases);
}

/** Old field id → current field id. */
export const FIELD_ALIASES: Readonly<Record<string, string>> =
  buildFieldAliases();

/** True when `field` is an old id that has a current replacement. */
export function isAliasedField(field: string): boolean {
  return Object.prototype.hasOwnProperty.call(FIELD_ALIASES, field);
}

/** Returns the current id for `field` (unchanged when it is not aliased). */
export function resolveFieldAlias(field: string): string {
  return isAliasedField(field) ? FIELD_ALIASES[field] : field;
}

/**
 * Resolves a list of field ids, keeping order and dropping duplicates created
 * by aliasing (first occurrence wins). Unknown ids are kept as they are, so
 * nothing a user saved is ever lost. A missing list becomes [].
 */
export function aliasFieldList(fields: readonly string[] | null | undefined) {
  const result: string[] = [];
  const seen = new Set<string>();
  (fields ?? []).forEach((field) => {
    const resolved = resolveFieldAlias(field);
    if (seen.has(resolved)) return;
    seen.add(resolved);
    result.push(resolved);
  });
  return result;
}

/**
 * Resolves the `field` of each item, keeping one item per resolved field. On
 * a collision an item that already used the current id beats one that used
 * an old id; otherwise the first item wins. For models that hold at most one
 * item per field (URL filters, sort models).
 */
export function aliasItemsUniqueByField<T extends { field: string }>(
  items: readonly T[]
): T[] {
  const byField = new Map<string, { item: T; current: boolean }>();
  items.forEach((item) => {
    const resolved = resolveFieldAlias(item.field);
    const current = resolved === item.field;
    const existing = byField.get(resolved);
    if (!existing || (current && !existing.current)) {
      byField.set(resolved, {
        item: current ? item : { ...item, field: resolved },
        current,
      });
    }
  });
  return Array.from(byField.values()).map(({ item }) => item);
}

function aliasItems<T extends { field: string }>(items: readonly T[]): T[] {
  return items.map((item) =>
    isAliasedField(item.field)
      ? { ...item, field: resolveFieldAlias(item.field) }
      : item
  );
}

/**
 * Resolves the fields of a MUI filter model. Items are only renamed, never
 * merged: a filter model may legitimately hold several items on one field.
 */
export function aliasFilterModel<
  T extends { items: readonly { field: string }[] }
>(model: T): T;
export function aliasFilterModel<
  T extends { items: readonly { field: string }[] }
>(model: T | null | undefined): T | null | undefined;
export function aliasFilterModel<
  T extends { items: readonly { field: string }[] }
>(model: T | null | undefined): T | null | undefined {
  if (!model || !Array.isArray(model.items)) return model;
  if (!model.items.some((item) => item && isAliasedField(item.field))) {
    return model;
  }
  return { ...model, items: aliasItems(model.items) };
}

/** Resolves the fields of a MUI sort model (one entry per field). */
export function aliasSortModel<T extends { field: string }>(
  model: readonly T[]
): T[];
export function aliasSortModel<T extends { field: string }>(
  model: readonly T[] | null | undefined
): T[] | null | undefined;
export function aliasSortModel<T extends { field: string }>(
  model: readonly T[] | null | undefined
): T[] | null | undefined {
  if (!Array.isArray(model)) return model as null | undefined;
  return aliasItemsUniqueByField(model);
}

/**
 * Resolves the fields of an advanced filter model ({ groups: [{ items }] }).
 * Items are only renamed, never merged.
 */
export function aliasAdvancedFilterModel<
  T extends { groups: readonly { items: readonly { field: string }[] }[] }
>(model: T | null): T | null {
  if (!model || !Array.isArray(model.groups)) return model;
  const hasAlias = model.groups.some(
    (group) =>
      Array.isArray(group?.items) &&
      group.items.some(
        (item: { field: string }) => item && isAliasedField(item.field)
      )
  );
  if (!hasAlias) return model;
  return {
    ...model,
    groups: model.groups.map((group) =>
      Array.isArray(group?.items)
        ? { ...group, items: aliasItems(group.items) }
        : group
    ),
  };
}

/**
 * Resolves the keys of a field-keyed record (e.g. a visibility map). On a
 * collision the value stored under the current id wins.
 */
export function aliasRecordKeys<V>(
  record: Record<string, V> | null | undefined
): Record<string, V> {
  const result: Record<string, V> = {};
  if (!record || typeof record !== "object") return result;
  Object.keys(record).forEach((key) => {
    if (!isAliasedField(key)) result[key] = record[key];
  });
  Object.keys(record).forEach((key) => {
    if (!isAliasedField(key)) return;
    const resolved = resolveFieldAlias(key);
    if (!Object.prototype.hasOwnProperty.call(result, resolved)) {
      result[resolved] = record[key];
    }
  });
  return result;
}

/** Resolves both column lists of a saved column set, keeping its shape. */
export function aliasColumnSet<
  T extends { listingColumns?: string[]; inventoryColumns?: string[] }
>(columnSet: T): T {
  if (!columnSet || typeof columnSet !== "object") return columnSet;
  return {
    ...columnSet,
    ...(Array.isArray(columnSet.listingColumns) && {
      listingColumns: aliasFieldList(columnSet.listingColumns),
    }),
    ...(Array.isArray(columnSet.inventoryColumns) && {
      inventoryColumns: aliasFieldList(columnSet.inventoryColumns),
    }),
  };
}
